import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskLines, EASE } from "./Reveal";
import { TESTIMONIALS } from "../data/content";

export const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const secRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: secRef, offset: ["start end", "end start"] });
  const bgWordX = useTransform(scrollYProgress, [0, 1], ["4%", "-6%"]);

  const go = (d) => {
    setDir(d);
    setIndex((i) => (i + d + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const t = TESTIMONIALS[index];

  return (
    <section ref={secRef} data-testid="testimonials-section" className="relative py-16 sm:py-36 overflow-hidden">
      <motion.span
        aria-hidden="true"
        style={{ x: bgWordX }}
        className="pointer-events-none select-none absolute top-1/2 -translate-y-1/2 left-0 font-display font-semibold tracking-[-0.04em] text-[26vw] leading-none text-charcoal/[0.035] whitespace-nowrap"
      >
        NAGEL
      </motion.span>

      <div className="relative px-6 sm:px-10 lg:px-16 max-w-[1700px] mx-auto">
        <div className="flex items-baseline justify-between mb-14 sm:mb-20">
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.28em] text-ember">(TESTIMONIALS)</p>
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.22em] text-charcoal/40">
            {String(index + 1).padStart(2, "0")} — {String(TESTIMONIALS.length).padStart(2, "0")}
          </p>
        </div>

        <MaskLines
          as="h2"
          lines={[<>Good work <span className="font-serif italic font-normal">speaks for itself.</span></>]}
          className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-charcoal mb-16 sm:mb-24"
        />

        <motion.div
          data-testid="testimonials-track"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.18}
          onDragEnd={(e, info) => {
            if (info.offset.x < -70) go(1);
            else if (info.offset.x > 70) go(-1);
          }}
          className="cursor-grab active:cursor-grabbing select-none"
        >
          <AnimatePresence mode="wait" custom={dir}>
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 44 * dir, clipPath: "inset(0 0 100% 0)" }}
              animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
              exit={{ opacity: 0, y: -34 * dir }}
              transition={{ duration: 0.7, ease: EASE }}
              className="max-w-5xl"
            >
              <span className="block font-display text-ember text-6xl sm:text-8xl leading-[0.4] mb-8">“</span>
              <p data-testid="testimonial-quote" className="font-serif italic tracking-[-0.01em] text-2xl sm:text-4xl lg:text-[3.2rem] leading-[1.18] text-charcoal">
                {t.quote}
              </p>
              <footer className="mt-10 flex items-center gap-4">
                <span className="w-10 h-px bg-flame" />
                <div>
                  <p className="font-mono text-xs tracking-[0.18em] text-charcoal">{t.author.toUpperCase()}</p>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-charcoal/45 mt-1">{t.org.toUpperCase()}</p>
                </div>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </motion.div>

        <div className="mt-14 flex items-center gap-4">
          <button
            data-testid="testimonial-prev-button"
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
            className="w-12 h-12 rounded-full border border-charcoal/20 flex items-center justify-center text-charcoal hover:bg-charcoal hover:text-cream transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            data-testid="testimonial-next-button"
            onClick={() => go(1)}
            aria-label="Next testimonial"
            className="w-12 h-12 rounded-full border border-charcoal/20 flex items-center justify-center text-charcoal hover:bg-charcoal hover:text-cream transition-colors duration-300"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="ml-4 flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i + 1}`}
                onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-1 rounded-full transition-all duration-500 ${i === index ? "w-10 bg-flame" : "w-4 bg-charcoal/15 hover:bg-charcoal/30"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
